<template>
  <div
    class="v-picture-thumbnail"
    :class="[size, { clickable: isClickable }]"
    @click="openInNewWindow"
  >
    <img
      v-if="dataUrl !== null"
      :src="dataUrl"
      :alt="entityPointer.name"
    />

    <VIcon
      v-else-if="isLoading"
      material="hourglass_empty"
    />

    <VIcon
      v-else-if="isFailed"
      material="broken_image"
    />

    <!-- Крупный файл: в память не тянем, миниатюры нет — открывается в новом окне -->
    <VIcon
      v-else
      material="photo"
    />

    <!-- Затемнение с глазом поверх миниатюры, проявляется по наведению -->
    <div
      v-if="isClickable"
      class="overlay"
    >
      <VIcon material="visibility"/>
    </div>
  </div>
</template>

<style lang="scss" src="./VPictureThumbnail.scss"/>

<script lang="ts">
import FilesDAO from "@/core/services/Files/FilesDAO";
import FileDescriptor from "@/core/services/Files/FileDescriptor";
import FilesService from "@/core/services/Files/FilesService";
import EntityPointer from "@/domains/EntityPointer/EntityPointer";

/**
 * Максимальный размер файла, для которого рисуется миниатюра.
 * Файлы крупнее не тянем в память — пользователь смотрит их в новом окне.
 */
const THUMBNAIL_MAX_SIZE = 2 * 1024 * 1024;

/** Размерный ряд миниатюры. Значение попадает в класс контейнера. */
const SIZES = ['small', 'medium', 'large'];

export default defineComponent({
  name: 'VPictureThumbnail',

  props: {
    /** Указатель на файл картинки. По нему компонент сам запрашивает описание и содержимое. */
    entityPointer: {
      type: Object as PropType<EntityPointer>,
      required: true,
    },

    /** Размер миниатюры: small 40×40, medium 90×90, large 150×150. */
    size: {
      type: String as PropType<'small' | 'medium' | 'large'>,
      default: 'large',
      validator: (value: string) => SIZES.includes(value),
    },
  },

  data() {
    return {
      /** data URL картинки. null — показывать нечего. */
      dataUrl: null as string | null,

      /** Идёт запрос — вместо картинки иконка-заглушка. */
      isLoading: true,

      /** Файл не найден, не картинка или запрос упал — иконка битого изображения. */
      isFailed: false,

      /** Описание файла с бека. Нужно, чтобы открыть картинку по клику. */
      fileDescriptor: null as FileDescriptor | null,
    };
  },

  async mounted() {
    await this.load();
  },

  computed: {
    /** Картинку можно открыть в новом окне — тогда рисуем оверлей с глазом. */
    isClickable(): boolean {
      if (this.fileDescriptor === null) {
        return false;
      } else {
        return this.fileDescriptor.isImage;
      }
    },
  },

  methods: {
    /**
     * Запрашивает описание файла по указателю и тянет содержимое в data URL.
     * Крупные файлы в память не тянем — для них остаётся только открытие в новом окне.
     */
    async load(): Promise<void> {
      try {
        const rpcResponse = await FilesDAO.getInfo(this.entityPointer.id);

        /** Файла нет на беке */
        if (rpcResponse.data === null) {
          this.isFailed = true;
          return;
        }

        this.fileDescriptor = FileDescriptor.fromServer(rpcResponse.data);

        /** Не картинка — миниатюру строить не из чего */
        if (this.fileDescriptor.isImage === false) {
          this.isFailed = true;
          return;
        }

        /** Крупный файл — не ошибка, просто без превью */
        if ((this.fileDescriptor.size ?? 0) > THUMBNAIL_MAX_SIZE) {
          return;
        }

        this.dataUrl = await FilesService.getFileAsDataUrl(this.fileDescriptor);
      } catch (error) {
        this.isFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    /** Открывает картинку в новом окне через FilesService. */
    openInNewWindow(): void {
      if (this.isClickable === false) {
        // открывать нечего — описания файла нет либо это не картинка
      } else {
        FilesService.previewFile(this.fileDescriptor);
      }
    },
  },
});
</script>
